import {
  IDexBuySellBotOptsType1w1s,
  IDexBuySellBotOptsType1wNs,
  IDexBuySellBotTaskItem,
  IDexBuySellBotTypeUnit,
  IDexBuySellBotWallet,
} from 'types/bots';
import { mapDexBuySellTaskType } from 'utils/dex/mapDexBuySellTaskType';

import { AApiBase } from '../apiBase/AApiBase';
import { mapError } from '../apiBase/mapError';
import { mapResponse } from '../apiBase/mapResponse';

type IDexBuySellBotOpts = IDexBuySellBotOptsType1wNs | IDexBuySellBotOptsType1w1s;

type BuySellSTasksResponse = {
  tasks: IDexBuySellBotTaskItem[] | undefined | null;
};

type BuySellSTaskWalletsResponse = {
  items: IDexBuySellBotWallet[];
  has_next: boolean;
};

type BuySellSTaskWalletQueryParams = {
  limit?: number;
  not_connected: string;
  balance?: string[] | undefined;
  balance_usd?: string[] | undefined;
  volume?: string[] | undefined;
  volume_usd?: string[] | undefined;
  transactions_count?: string[] | undefined;
};

class ApiDexBuySellBot extends AApiBase {
  protected baseUrl = '/api';
  protected withHeader = true;

  private static __instance: ApiDexBuySellBot | undefined;
  private static get instance(): ApiDexBuySellBot {
    if (ApiDexBuySellBot.__instance === undefined) {
      ApiDexBuySellBot.__instance = new ApiDexBuySellBot();
    }
    return ApiDexBuySellBot.__instance;
  }

  public static getBuySellTasks = async ({ pairId }: { pairId: number }) => {
    try {
      const response = await ApiDexBuySellBot.instance.get<BuySellSTasksResponse>(
        `/buysellbot-tasks?pair_id=${pairId}`,
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error) {
      return mapError<BuySellSTasksResponse>({ error });
    }
  };

  public static getBuySellTask = async ({ taskId }: { taskId: number }) => {
    try {
      const response = await ApiDexBuySellBot.instance.get<IDexBuySellBotTaskItem | undefined>(
        `/buysellbot-tasks/${taskId}`,
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error) {
      return mapError<IDexBuySellBotTaskItem | undefined>({ error });
    }
  };

  public static createBuySellTask = async ({
    type,
    pairId,
    options,
  }: {
    type: IDexBuySellBotTypeUnit;
    pairId: number;
    options: IDexBuySellBotOpts;
  }) => {
    try {
      const response = await ApiDexBuySellBot.instance.post<{ id: number }>('/buysellbot-tasks', {
        pair_id: pairId,
        [mapDexBuySellTaskType(type)]: options,
        type,
      });

      return mapResponse({
        data: response.data,
      });
    } catch (error) {
      return mapError<{ id: number }>({ error });
    }
  };

  public static updateBuySellTask = async ({
    type,
    taskId,
    options,
  }: {
    type: IDexBuySellBotTypeUnit;
    taskId: number;
    options: IDexBuySellBotOpts;
  }) => {
    try {
      const response = await ApiDexBuySellBot.instance.patch(`/buysellbot-tasks/${taskId}`, {
        type,
        [mapDexBuySellTaskType(type)]: options,
      });

      return mapResponse({
        data: response.data,
      });
    } catch (error) {
      return mapError<any>({ error });
    }
  };

  public static deleteBuySellTask = async ({ taskId }: { taskId: number }) => {
    try {
      const response = await ApiDexBuySellBot.instance.delete(`/buysellbot-tasks/${taskId}`);

      return mapResponse({
        data: response.data,
      });
    } catch (error) {
      return mapError({ error });
    }
  };

  public static getBuySellTaskWallets = async ({
    taskId,
    params: { limit, not_connected, balance, balance_usd, volume, volume_usd, transactions_count },
  }: {
    taskId: number;
    params: BuySellSTaskWalletQueryParams;
  }) => {
    try {
      const urlParams = new URLSearchParams();

      limit && urlParams.append('limit', limit.toString());
      not_connected && urlParams.append('not_connected', not_connected.toString());

      if (balance) {
        for (const _balance of balance) {
          urlParams.append('balance', _balance);
        }
      }
      if (balance_usd) {
        for (const _balance of balance_usd) {
          urlParams.append('balance_usd', _balance);
        }
      }

      if (volume) {
        for (const _volume of volume) {
          urlParams.append('volume', _volume);
        }
      }
      if (volume_usd) {
        for (const _volume of volume_usd) {
          urlParams.append('volume_usd', _volume);
        }
      }

      if (transactions_count) {
        for (const _tx_count of transactions_count) {
          urlParams.append('transactions_count', _tx_count);
        }
      }

      const response = await ApiDexBuySellBot.instance.get<BuySellSTaskWalletsResponse>(
        `/buysellbot-tasks/${taskId}/wallets?${urlParams.toString()}`,
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error) {
      return mapError<BuySellSTaskWalletsResponse>({ error });
    }
  };

  public static addWalletsToBuySellTask = async ({
    taskId,
    walletIds,
  }: {
    taskId: number;
    walletIds: number[];
  }) => {
    try {
      const response = await ApiDexBuySellBot.instance.put<BuySellSTaskWalletsResponse>(
        `/buysellbot-tasks/${taskId}/wallets`,
        { wallet_ids: walletIds },
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error) {
      return mapError<BuySellSTaskWalletsResponse>({ error });
    }
  };

  public static removeWalletsFromBuySellTask = async ({
    taskId,
    walletIds,
  }: {
    taskId: number;
    walletIds: number[];
  }) => {
    try {
      const response = await ApiDexBuySellBot.instance.delete(
        `/buysellbot-tasks/${taskId}/wallets`,
        { data: { wallet_ids: walletIds } },
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error) {
      return mapError({ error });
    }
  };
}

export { ApiDexBuySellBot };
